import { jsPlumb } from "jsplumb";

import ApplicationController from "./application_controller"

const ARROW_CONNECTION_GAP = 15;

export default class extends ApplicationController {

  static targets = [
    'spellingLinkingCheckbox'
  ]

  initialize() {
    this.selectedSpellingCheckboxes = [];
    this.arrowConnections           = {};
    this.currentConnectionGaps      = { horizontalGap: 0, verticalGap: 0 };

    // This is the main container used for drawing and redrawing arrows for connections on scroll, along with other arrow-related functionalities.
    this.plumbContainer             = document.querySelector(".js-plumb-container");
    this.initializeJsPlumb();
    window['typing_fields_arrow_connector'] = this;
  }

  // This method initializes Plumb
  initializeJsPlumb() {
    jsPlumb.ready(() => {
      // const container = document.querySelector(".js-plumb-container");
      if (this.plumbContainer) {
        this.jsPlumbInstance = jsPlumb.getInstance({
          Container: this.plumbContainer
        });

        // This method draws the arrows for linkages when an item is navigated, it checks all linkages and draws arrows only for those that are not immediately above or below the field.
        this.drawPersistedConnections();
        
        // Handle click/right-click events to remove connections. Arrow connection is removed by clicking/right-clicking the Arrow 
        this.jsPlumbInstance.bind("click", this.removeArrowConnection.bind(this));
        this.jsPlumbInstance.bind("contextmenu", this.removeArrowConnection.bind(this));  // to handle right click case     
      } else {
        console.error("jsPlumb container not found!");
      }
    });
  }

  // This method refreshes the JsPlumb instance by:
  // - Revalidating the container element to update JsPlumb's internal state.
  // - Recalculating offsets to ensure accurate positioning of connections.
  // - Repainting all connections and elements to reflect any changes.  
  refreshJsPlumb() {
    // Revalidate the container element to refresh jsPlumb's internal state
    this.jsPlumbInstance.revalidate(this.plumbContainer);
    this.jsPlumbInstance.recalculateOffsets();
    this.jsPlumbInstance.repaintEverything();
  }

  resetConnectionGaps(){
    this.currentConnectionGaps = { horizontalGap: 0, verticalGap: 0 };
  }

  decreaseConnectionArrowSpacing() {
    // Decrease horizontal and vertical gaps by 15, ensuring they don't go below zero
    this.currentConnectionGaps.horizontalGap = Math.max(0, this.currentConnectionGaps.horizontalGap - ARROW_CONNECTION_GAP);
    this.currentConnectionGaps.verticalGap   = Math.max(0, this.currentConnectionGaps.verticalGap - ARROW_CONNECTION_GAP);
  }

  removeArrowConnection(connection, event = null) {
    if (event) {
      event.preventDefault(); // To prevent default action on right-click
    }

    // Get the connection checkboxes
    const sourceCheckbox = connection.source;
    const targetCheckbox = connection.target;

    const sourceId       = sourceCheckbox.getAttribute('data-spelling-linkage-checkbox');

    this.jsPlumbInstance.deleteConnection(connection);
    if (this.arrowConnections[sourceId]) {
        delete this.arrowConnections[sourceId];
    }

    this.resetExplanationSpellingTempId(sourceCheckbox);
    this.enableDragForDisconnectedFields(sourceCheckbox, targetCheckbox);   
    this.decreaseConnectionArrowSpacing();
    this.removeBlueDividerAboveExplainedSpellingField(sourceCheckbox);
  }  

  // Remove all connections but keep the source id in this.connections to allow redrawing of connections
  removeExistingArrowConnections() {
    this.jsPlumbInstance.deleteEveryConnection();
    this.spellingLinkingCheckboxTargets.forEach(checkbox => {
      this.resetExplanationSpellingTempId(checkbox);
    });
    this.removeAllBlueDividersAboveExplainedSpellingFields();
  } 

  removeAllConnectionsRelatedToField(event) {
    const spellingField = event.target.closest('.js-entries-input');
    if (!spellingField) return;

    const spellingFieldCheckbox = spellingField.querySelector('.js-spelling-linkage-checkbox');
    if (!spellingFieldCheckbox) return;

    const checkboxId = spellingFieldCheckbox.getAttribute('data-spelling-linkage-checkbox');
    if (!checkboxId) return;

    // Get all connections where this element is either a source or a target
    const connectionsAsSource = this.jsPlumbInstance.getConnections({ source: spellingFieldCheckbox });
    const connectionsAsTarget = this.jsPlumbInstance.getConnections({ target: spellingFieldCheckbox });

    // Combine both sets of connections
    const deletedFieldAllConnections = [...connectionsAsSource, ...connectionsAsTarget];

    // Early return if there are no connections
    if (deletedFieldAllConnections.length === 0) return;

    // Remove each connection
    deletedFieldAllConnections.forEach(connection => {
      this.removeArrowConnection(connection); // Manually trigger removal   
    });

    // this.redrawFieldConnections();
  } 

  // This method should be called on actions which reorder a field, such as reordering fields through drag handle, adding a new field between existing ones, or deleting a field.
  redrawFieldConnections() {
    this.removeExistingArrowConnections();
    this.resetConnectionGaps();
    this.unhideAllLinkageCheckboxes();
    this.refreshJsPlumb();

    const spellingOrderNumbers = this.getSortedSpellingOrderNumbers();

    Object.entries(this.arrowConnections).forEach(([sourceId, targetId]) => {
      const source = document.querySelector(`[data-spelling-linkage-checkbox="${sourceId}"]`);
      const target = document.querySelector(`[data-spelling-linkage-checkbox="${targetId}"]`);

      if (source && target) {
        const sourceOrderNumber = this.checkboxFieldOrderNumber(source);
        const targetOrderNumber = this.checkboxFieldOrderNumber(target);

        const currentIndex = spellingOrderNumbers.indexOf(sourceOrderNumber);
        const immediatePrevious = currentIndex > 0 ? spellingOrderNumbers[currentIndex - 1] : null;
        const immediateNext = currentIndex < spellingOrderNumbers.length - 1 ? null : spellingOrderNumbers[currentIndex + 1];

        // Check if the connection is for the immediate above or below field,
        // or if the explanation field is positioned below the explained field.
        // If any of these conditions are met, skip the connection creation process.
        if (targetOrderNumber === immediatePrevious || targetOrderNumber === immediateNext || targetOrderNumber >= sourceOrderNumber) {
          this.enableDragForDisconnectedFields(source, target)

          if (this.arrowConnections[sourceId]) {
              delete this.arrowConnections[sourceId];
          }
          
          return;
        }

        this.createJsPlumbConnection(source, target);
      } else {
        console.error("Source or target element not found:", { sourceId, targetId });
      }
    });

    this.jsPlumbInstance.repaintEverything();
  }

  // Each spelling field has a hidden field storing the temp ID of its explanation spelling.  
  // This method retrieves the explanation spelling field container using the temp ID  
  // and then gets the explanation field's order number.
  getExplanationSpellingOrderNumber(spelling) {
    const explanationSpellingTemporaryId = spelling.querySelector('.js-explanation-spelling-temp-reference-id')?.value || null;
    if (!explanationSpellingTemporaryId) return null;
    
    const spellings = typeo_form.allSpellingFields();
    for (const spellingItem of spellings) {
      const spellingTempId = spellingItem.querySelector('.js-spelling-temp-reference-id')?.value;
      if (spellingTempId === explanationSpellingTemporaryId) {
        return spellingItem.querySelector('.js-otto-spelling-assigned-number')?.value || null;
      }
    }
    
    return null;
  }

  // Helper method for a repeated functionality.  
  getSortedSpellingOrderNumbers() {
    const spellings = typeo_form.allSpellingFields();

    // Step 2: Filter out hidden spellings that are marked for deletion
    const visibleSpellings = spellings.filter(spelling => {
      const wrapper = spelling.closest('.js-nested-form-wrapper');
      return !(wrapper && getComputedStyle(wrapper).display === "none"); // Keep only visible spellings
    });

    // Step 3: Extract order numbers from visible spellings
    const orderNumbers = visibleSpellings.map(spelling => {
      const orderField = spelling.querySelector('.js-otto-spelling-assigned-number');
      return orderField ? parseInt(orderField.value, 10) : null;
    });

    // Remove null values
    const validNumbers = orderNumbers.filter(num => num !== null);

    // Sort numbers in ascending order
    return validNumbers.sort((a, b) => a - b);   
  }

  // This method is called when an item is navigated from initialize method
  // To draw the arrows for linkages when an item is navigated, it checks all linkages and draws arrows only for those that are not immediately above or below the field.
  drawPersistedConnections() {
    this.currentConnectionGaps = { horizontalGap: 0, verticalGap: 0 };
    const spellingOrderNumbers = this.getSortedSpellingOrderNumbers();
    const spellings            = typeo_form.allSpellingFields();

    spellings.forEach(spelling => {
      const hiddenFieldForCurrentSpellingOrderNumber = spelling.querySelector('.js-otto-spelling-assigned-number');
      const explanationSpellingOrderNumber           = this.getExplanationSpellingOrderNumber(spelling);

      if (hiddenFieldForCurrentSpellingOrderNumber && explanationSpellingOrderNumber !== "") {
        const currentSpellingOrderNumber             = parseInt(hiddenFieldForCurrentSpellingOrderNumber.value, 10);
        const requiredExplanationSpellingOrderNumber = parseInt(explanationSpellingOrderNumber, 10);

        // Find the immediate previous and next spelling order numbers
        const currentIndex      = spellingOrderNumbers.indexOf(currentSpellingOrderNumber);
        const immediatePrevious = currentIndex > 0 ? spellingOrderNumbers[currentIndex - 1] : null;
        const immediateNext     = currentIndex < spellingOrderNumbers.length - 1 ? spellingOrderNumbers[currentIndex + 1] : null;

        // Skip if the required explanation spelling order number is the immediate previous or next
        if (requiredExplanationSpellingOrderNumber === immediatePrevious || requiredExplanationSpellingOrderNumber === immediateNext) {
          return; // Skip this spelling
        }

        // Find the spelling field where the required explanation value matches
        const explanationSpellingField = spellings.find(explanationSpelling => {
          const explanationFieldOrderNumber = explanationSpelling.querySelector('.js-otto-spelling-assigned-number');
          return explanationFieldOrderNumber && parseInt(explanationFieldOrderNumber.value, 10) === requiredExplanationSpellingOrderNumber;
        });

        if (explanationSpellingField) {
          // Get checkboxes inside both spellings(the explained and the explanation)
          const explainedCheckbox = spelling.querySelector('.js-spelling-linkage-checkbox');
          const explanationCheckbox = explanationSpellingField.querySelector('.js-spelling-linkage-checkbox');

          if (explainedCheckbox && explanationCheckbox) {
            this.createJsPlumbConnection(explainedCheckbox, explanationCheckbox);
          }
        }
      }
    });
  }

  // This method returns the order number of the spelling field associated with the checkbox.
  checkboxFieldOrderNumber(checkBox) {
    const checkBoxFieldContainer = checkBox.closest('.js-entries-input');
    if (!checkBoxFieldContainer) return null;

    const orderNumber = checkBoxFieldContainer.querySelector('.js-otto-spelling-assigned-number');
    return orderNumber ? parseInt(orderNumber.value, 10) : null;    
  } 

  // This method is called when a checkbox is checked/selected.
  // Method to keep the track of selected checkboxes - this method ensures that at most two checkboxes can be selected at a time, if a third checkbox is selected, the earliest selected one is then unchecked
  handleSpellingLinkageCheckboxStateChange(event) {
    const checkbox          = event.currentTarget;
    const linkageCheckboxId = checkbox.dataset.spellingLinkageCheckbox;

    if (checkbox.checked) {
      this.selectedSpellingCheckboxes.push(linkageCheckboxId);
    } else {
      this.selectedSpellingCheckboxes = this.selectedSpellingCheckboxes.filter(id => id !== linkageCheckboxId);
    }

    // Keep only two selections
    if (this.selectedSpellingCheckboxes.length > 2) {
      const removedCheckBox   = this.selectedSpellingCheckboxes.shift();
      const checkBoxToUncheck = this.spellingLinkingCheckboxTargets.find(cb => cb.dataset.spellingLinkageCheckbox === removedCheckBox);
      if (checkBoxToUncheck) checkBoxToUncheck.checked = false;
    }
  }  

  // This method resets the Explanation Spelling Order Number in the hidden field when a connected is removed, either by clicking an arrow or through code on certain actions (it resets the hidden field value having the explanaion spelling temp id)
  resetExplanationSpellingTempId(explainedSpellingCheckBox) {
    const explainedSpellingFieldContainer = explainedSpellingCheckBox.closest('.js-entries-input');
    
    if (explainedSpellingFieldContainer) {
      const hiddenFieldForExplanationSpellingTempId = explainedSpellingFieldContainer.querySelector('.js-explanation-spelling-temp-reference-id');
      if (hiddenFieldForExplanationSpellingTempId) {
        hiddenFieldForExplanationSpellingTempId.value = ''; // Reset value to empty string
      }
    }
  } 

  // Sets the value of explanation_spelling_temp_id in the source spelling(explained spelling) fields
  setExplanationSpellingTempId(explainedSpellingCheckBox, explanationSpellingCheckBox) {
    const explanationSpellingFieldContainer = explanationSpellingCheckBox.closest('.js-entries-input');
    if (explanationSpellingFieldContainer) {
      const explanationSpellingTempId = explanationSpellingFieldContainer.querySelector('.js-spelling-temp-reference-id')?.value;

      // Set the explanation_spelling_temp_id value in explainedSpellingCheckBox's associated input
      const explainedSpellingFieldContainer = explainedSpellingCheckBox.closest('.js-entries-input');
      if (explainedSpellingFieldContainer && explanationSpellingTempId) {
        const hiddenFieldForExplanationSpellingTempId = explainedSpellingFieldContainer.querySelector('.js-explanation-spelling-temp-reference-id');
        
        if (hiddenFieldForExplanationSpellingTempId) {
          hiddenFieldForExplanationSpellingTempId.value = explanationSpellingTempId;
        }
      }
    }
  }

  enableDragForDisconnectedFields(explainedSpellingCheckBox, explanationSpellingCheckBox) {
    [explainedSpellingCheckBox, explanationSpellingCheckBox].forEach(checkbox => {
      const spellingFieldContainer = checkbox.closest('.js-entries-input');
      if (spellingFieldContainer) {
        const dragHandle = spellingFieldContainer.querySelector('.js-drag-handle');
        if (dragHandle) {
          dragHandle.classList.remove('d-none');
        }
      }
    });    
  }

  disableDragForArrowConnectedFields(explainedSpellingCheckBox, explanationSpellingCheckBox) {
    [explainedSpellingCheckBox, explanationSpellingCheckBox].forEach(checkbox => {
      const spellingFieldContainer = checkbox.closest('.js-entries-input');
      if (spellingFieldContainer) {
        const dragHandle = spellingFieldContainer.querySelector('.js-drag-handle');
        if (dragHandle) {
          dragHandle.classList.add('d-none');
        }
      }
    });    
  }

  // This method is called when the Enter key is pressed after selecting checkboxes on two fields
  handleSpellingsLinkingThroughArrows() {
    event.preventDefault();
    this.redrawFieldConnections();

    const [firstCheckbox, secondCheckbox] = this.selectedSpellingCheckboxes;

    const firstCheckboxElement  = this.spellingLinkingCheckboxTargets.find(cb => cb.dataset.spellingLinkageCheckbox === firstCheckbox);
    const secondCheckboxElement = this.spellingLinkingCheckboxTargets.find(cb => cb.dataset.spellingLinkageCheckbox === secondCheckbox);

    const firstCheckboxOrderNum  = this.checkboxFieldOrderNumber(firstCheckboxElement)
    const secondCheckboxOrderNum = this.checkboxFieldOrderNumber(secondCheckboxElement)

    let explainedSpellingCheckBox, explanationSpellingCheckBox;

    if (firstCheckboxOrderNum > secondCheckboxOrderNum) {
      explainedSpellingCheckBox   = firstCheckboxElement;
      explanationSpellingCheckBox = secondCheckboxElement;
    } else {
      explainedSpellingCheckBox   = secondCheckboxElement;
      explanationSpellingCheckBox = firstCheckboxElement;
    }    

    if (explainedSpellingCheckBox && explanationSpellingCheckBox) {
      this.createJsPlumbConnection(explainedSpellingCheckBox, explanationSpellingCheckBox);
    }

    // Clear the checkboxes selection
    this.selectedSpellingCheckboxes = [];
    this.spellingLinkingCheckboxTargets.forEach(checkbox => (checkbox.checked = false));
  } 

  increaseConnectionArrowSpacing() {
    this.currentConnectionGaps.horizontalGap += ARROW_CONNECTION_GAP;
    this.currentConnectionGaps.verticalGap += ARROW_CONNECTION_GAP;
  }

  removeExistingConnectionIfAny(sourceId, source) {
    if (this.arrowConnections[sourceId]) {
      const existingConnection = this.jsPlumbInstance.getConnections({ source })[0]; // Get the first (and only) connection
      if (existingConnection) {
        this.removeArrowConnection(existingConnection); // Manually trigger removal
      }
      delete this.arrowConnections[sourceId]; // Remove previous source connection
    }
  }

  createJsPlumbConnection(source, target) {
    this.increaseConnectionArrowSpacing();
    const { horizontalGap, verticalGap } = this.currentConnectionGaps;

    // Ensure jsPlumbInstance is initialized before proceeding
    if (!this.jsPlumbInstance) {
      console.error("jsPlumbInstance is not initialized!");
      return;
    }

    // Validate that both source and target elements are provided
    if (!source || !target) {
      console.error("Invalid source or target:", { source, target });
      return;
    }

    // Get the source and target's unique ids
    const sourceId = source.getAttribute('data-spelling-linkage-checkbox');
    const targetId = target.getAttribute('data-spelling-linkage-checkbox');

    // Since a spelling field cannot be linked to more than one spelling above it, any new connection attempt should replace the previous connection.
    this.removeExistingConnectionIfAny(sourceId, source);

    // Count how many sources are already connected to this target to get the connections count on this target for spacing adjustments between the arrows
    const connectionsCountOnTargetElement = Object.values(this.arrowConnections).filter(id => id === targetId).length;

    // Create the jsPlumb connection between source and target
    // Stub adjustment: If the arrow initially looks like this (->), increasing the stub will extend it to (--->).
    // Offset adjustment: It increases the vertical spacing between arrows, moving them up or down.
    const connection = this.jsPlumbInstance.connect({
      source: source,
      target: target,
      anchors: [
        ["Left", { offset: verticalGap + (connectionsCountOnTargetElement*15) }], // Adjust anchor position based on connection count
        ["Left", { offset: verticalGap + (connectionsCountOnTargetElement*15) }]
      ],
      connector: ["Flowchart", { stub: 20 + horizontalGap, gap: 16, alwaysRespectStubs: true, cornerRadius: 10 }], // Adjust gap to prevent overlap
      paintStyle: { stroke: "#3e5a76", strokeWidth: 5 }, // Arrow styling
      endpoint: "Blank",
      overlays: [
        [
          "Arrow",
          { width: 30, length: 10, location: 1, paintStyle: { fill: "#3e5a76", strokeWidth: 5 }, id: "arrow-overlay" }
        ]
      ]
    });

    // Store the new connection
    this.arrowConnections[sourceId] = targetId;
    this.unhideAllLinkageCheckboxes();
    this.disableDragForArrowConnectedFields(source, target);
    this.setExplanationSpellingTempId(source, target); 
    this.addBlueDividerAboveExplainedSpellingField(source)  
  }

  addBlueDividerAboveExplainedSpellingField(checkBox) {
    const otherValueContainer                        = checkBox.closest('.js-other-value-container');
    const blueDividerAboveExplainedSpellingContainer = otherValueContainer.querySelector('.js-blue-divider-above-explained-spelling');
    blueDividerAboveExplainedSpellingContainer.classList.add('blue-border-bottom')    
  }

  removeBlueDividerAboveExplainedSpellingField(checkBox) {
    const otherValueContainer                        = checkBox.closest('.js-other-value-container');
    const blueDividerAboveExplainedSpellingContainer = otherValueContainer.querySelector('.js-blue-divider-above-explained-spelling');
    blueDividerAboveExplainedSpellingContainer.classList.remove('blue-border-bottom')    
  }

  removeAllBlueDividersAboveExplainedSpellingFields() {
    const otherValueContainers = document.querySelectorAll('.js-other-value-container');
    otherValueContainers.forEach(container => {
      const blueDivider = container.querySelector('.js-blue-divider-above-explained-spelling');
      blueDivider?.classList.remove('blue-border-bottom');
    });    
  }

  repaintJsPlumb() {
    jsPlumb.repaintEverything();
  }

  disableCheckBoxInAboveAndBelowFields(event) {
    // Get the checkbox that was clicked
    const checkbox = event.target;

    // Find the closest spelling field container of the clicked checkbox
    const spellingFieldOfClickedCheckBox = checkbox.closest('.js-entries-input'); // Find the closest spelling field
    if (!spellingFieldOfClickedCheckBox) return;

    // Get the hidden field that stores the order number of this spelling field
    const hiddenFieldForOrderNumberOfClickedField = spellingFieldOfClickedCheckBox.querySelector('.js-otto-spelling-assigned-number');
    if (!hiddenFieldForOrderNumberOfClickedField) return;

    // Get the order number of the clicked checkbox's spelling field
    const clickedCheckBoxFieldOrderNumber = parseInt(hiddenFieldForOrderNumberOfClickedField.value, 10);

    const spellingsFields = typeo_form.allSpellingFields();

    let closestLower = null, closestHigher = null;
    let closestLowerDiff = Infinity, closestHigherDiff = Infinity;

    // Loop through all spelling fields to find the closest ones above and below
    spellingsFields.forEach((spellingField) => {
      const hiddenFieldForOrderNumber = spellingField.querySelector('.js-otto-spelling-assigned-number');
      const orderNumberOfSpellingField = parseInt(hiddenFieldForOrderNumber?.value, 10);
      
      // Skip the field that matches the clicked checkbox's order number
      if (orderNumberOfSpellingField === clickedCheckBoxFieldOrderNumber) return;

      const diff = Math.abs(orderNumberOfSpellingField - clickedCheckBoxFieldOrderNumber);

      // Find the closest above field
      if (orderNumberOfSpellingField < clickedCheckBoxFieldOrderNumber && diff < closestLowerDiff) {
        closestLower = spellingField;
        closestLowerDiff = diff;
      }

      // Find the closest below field
      if (orderNumberOfSpellingField > clickedCheckBoxFieldOrderNumber && diff < closestHigherDiff) {
        closestHigher = spellingField;
        closestHigherDiff = diff;
      }
    });

    // If the checkbox is checked, hide checkboxes in the closest above and below fields
    if (checkbox.checked) {
      if (closestLower) {
        const lowerCheckbox = closestLower.querySelector('.js-spelling-linkage-checkbox');
        if (lowerCheckbox) lowerCheckbox.classList.add('d-none');
      }
      if (closestHigher) {
        const higherCheckbox = closestHigher.querySelector('.js-spelling-linkage-checkbox');
        if (higherCheckbox) higherCheckbox.classList.add('d-none');
      }
    } else {
      // If the checkbox is unchecked, unhide the checkboxes in the closest above and below fields
      if (closestLower) {
        const lowerCheckbox = closestLower.querySelector('.js-spelling-linkage-checkbox');
        if (lowerCheckbox) lowerCheckbox.classList.remove('d-none');
      }
      if (closestHigher) {
        const higherCheckbox = closestHigher.querySelector('.js-spelling-linkage-checkbox');
        if (higherCheckbox) higherCheckbox.classList.remove('d-none');
      }
    }
  }

  unhideAllLinkageCheckboxes() {
    const spellingsFields = typeo_form.allSpellingFields();

    spellingsFields.forEach(spellingField => {
      const linkageCheckbox = spellingField.querySelector('.js-spelling-linkage-checkbox');
      if (linkageCheckbox) {
        linkageCheckbox.classList.remove('d-none');
      }
    });
  }
}